'use client';

import * as React from 'react';
import toast from 'react-hot-toast';
import {
  getScreenShareQualityLabel,
  readScreenShareQualityProfile,
  SCREEN_SHARE_QUALITY_CHANGED_EVENT,
  writeScreenShareQualityProfile,
  type ScreenShareQualityProfile,
} from './screen-share-profile';

type ScreenQualityContextValue = {
  screenQualityProfile: ScreenShareQualityProfile;
  setScreenQualityProfile: (profile: ScreenShareQualityProfile) => void;
};

const ScreenQualityContext = React.createContext<ScreenQualityContextValue | null>(null);

export function ScreenQualityProvider({
  children,
  initialProfile,
}: {
  children: React.ReactNode;
  initialProfile?: ScreenShareQualityProfile;
}) {
  const [screenQualityProfile, setProfileState] = React.useState<ScreenShareQualityProfile>(
    initialProfile ?? 'normal',
  );

  React.useEffect(() => {
    setProfileState(initialProfile ?? readScreenShareQualityProfile());
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ profile?: ScreenShareQualityProfile }>).detail;
      if (detail?.profile) {
        setProfileState(detail.profile);
      } else {
        setProfileState(readScreenShareQualityProfile());
      }
    };
    window.addEventListener(SCREEN_SHARE_QUALITY_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(SCREEN_SHARE_QUALITY_CHANGED_EVENT, onChange);
  }, [initialProfile]);

  const setScreenQualityProfile = React.useCallback((profile: ScreenShareQualityProfile) => {
    writeScreenShareQualityProfile(profile);
    setProfileState(readScreenShareQualityProfile());
    toast(`Qualidade da tela: ${getScreenShareQualityLabel(readScreenShareQualityProfile())}`, {
      icon: '🖥️',
      duration: 3500,
    });
  }, []);

  const value = React.useMemo(
    () => ({ screenQualityProfile, setScreenQualityProfile }),
    [screenQualityProfile, setScreenQualityProfile],
  );

  return (
    <ScreenQualityContext.Provider value={value}>{children}</ScreenQualityContext.Provider>
  );
}

export function useScreenQuality(): ScreenQualityContextValue {
  const ctx = React.useContext(ScreenQualityContext);
  if (!ctx) {
    throw new Error('useScreenQuality must be used within ScreenQualityProvider');
  }
  return ctx;
}

export function useOptionalScreenQuality(): ScreenQualityContextValue | null {
  return React.useContext(ScreenQualityContext);
}
